/*
  Desktop main menu (snippets/header-menu-pro.liquid).

  The parent stays an ordinary link, so the panel can't be tied to a click on it. Hover
  opens it for mouse users, the caret button opens it for keyboard and touch, and both
  paths set aria-expanded on that button.
*/
class HeaderMenuBar extends HTMLElement {
  constructor() {
    super();
    this.openDelay = 90;
    this.closeDelay = 220;
    this.openTimer = null;
    this.closeTimer = null;
    this.current = null;
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.hmenu__item--has-panel'));
    if (!this.items.length) return;

    // Hover is only for devices that really have a pointer; a tap fires pointerenter too,
    // and opening a panel under the finger that is about to follow a link is confusing.
    this.canHover =
      this.hasAttribute('data-open-on-hover') &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.items.forEach((item) => {
      const toggle = item.querySelector('.hmenu__toggle');
      const panel = item.querySelector('[data-hmenu-panel]');
      if (!toggle || !panel) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen(item) ? this.close(item) : this.open(item);
      });

      toggle.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.open(item);
          this.focusFirstLink(panel);
        }
      });

      if (this.canHover) {
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.cancelClose();
          this.openTimer = setTimeout(() => this.open(item), this.openDelay);
        });

        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.openTimer);
          this.scheduleClose(item);
        });
      }

      // Tabbing out of the item closes it, but moving between its own links must not.
      item.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) this.close(item);
        });
      });

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen(item)) return;
        event.stopPropagation();
        this.close(item);
        toggle.focus();
      });
    });

    this.onDocumentClick = (event) => {
      if (this.current && !this.current.contains(event.target)) this.closeAll();
    };
    document.addEventListener('click', this.onDocumentClick);

    this.onResize = () => this.closeAll();
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  isOpen(item) {
    return item.classList.contains('is-open');
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
  }

  scheduleClose(item) {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.close(item), this.closeDelay);
  }

  open(item) {
    this.cancelClose();
    if (this.current && this.current !== item) this.close(this.current);

    const panel = item.querySelector('[data-hmenu-panel]');
    const toggle = item.querySelector('.hmenu__toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    this.current = item;

    if (!panel) return;
    clearTimeout(panel.hideTimer);
    panel.style.display = 'block';
    // Lay the panel out first, so the class change animates instead of appearing finished.
    requestAnimationFrame(() => {
      if (this.current !== item) return;
      item.classList.add('is-open');
      this.keepInViewport(panel);
    });
  }

  close(item) {
    if (!item) return;
    item.classList.remove('is-open');
    const toggle = item.querySelector('.hmenu__toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    const panel = item.querySelector('[data-hmenu-panel]');
    if (panel) {
      panel.style.removeProperty('--hmenu-shift');
      clearTimeout(panel.hideTimer);
      panel.hideTimer = setTimeout(() => {
        if (!item.classList.contains('is-open')) panel.style.display = '';
      }, 220);
    }
    if (this.current === item) this.current = null;
  }

  closeAll() {
    this.items.forEach((item) => this.close(item));
  }

  // The panel is as wide as its content, so one opened near the right edge would overflow.
  keepInViewport(panel) {
    if (!panel) return;
    panel.style.removeProperty('--hmenu-shift');
    const rect = panel.getBoundingClientRect();
    const margin = 16;
    const overflowRight = rect.right - (document.documentElement.clientWidth - margin);
    if (overflowRight > 0) {
      panel.style.setProperty('--hmenu-shift', `-${Math.ceil(overflowRight)}px`);
      return;
    }
    if (rect.left < margin) {
      panel.style.setProperty('--hmenu-shift', `${Math.ceil(margin - rect.left)}px`);
    }
  }

  focusFirstLink(panel) {
    const link = panel.querySelector('a[href]');
    if (link) link.focus();
  }
}

customElements.define('header-menu-bar', HeaderMenuBar);
